import lodash from "lodash";

// 遍历对象
export function forObj(obj, fn) {
  Object.entries(obj).forEach(([k, v]) => {
    fn(v, k, obj);
  });
}

// 序列化
export function serialize(obj, fn = (v) => v) {
  const arr: any = [];
  forObj(obj, (v, k) => {
    arr.push(`${k}=${fn(v)}`);
  });
  return arr.join("&");
}

// 反序列化
export function reserize(str = location.search, fn = (v: any) => v): any {
  str = str.replace(/^\?/, "");
  const obj: any = {};
  str.split("&").forEach((i) => {
    const [k, v] = i.split("=");
    obj[k] = fn ? fn(v) : v;
  });
  return obj;
}

// 遍历
export function traverse<T extends Object, U extends TreeProp<T>>(
  arr: T[],
  callback: (item: T, opt: { pipe: T[]; parent?: T; level?: number }) => any,
  children: U = "children" as any,
  pop = false,
  pipe: T[] = [],
  parent: T | undefined = undefined,
  level: number = 0
) {
  arr.forEach((v) => {
    pipe.push(v);
    pop || callback(v, { pipe, parent, level });
    if (Array.isArray(v[children])) {
      level++;
      traverse(v[children], callback, children, pop, pipe, v, level);
      level--;
    }
    pop && callback(v, { pipe, parent, level });
    pipe.pop();
  });
  return arr;
}

export type Tree<T = Object> = T & { children?: Tree<T>[] };

type TreeProp<T> = Required<{
  [P in keyof T]: T[P] extends T[] | undefined
    ? T[P] extends undefined
      ? never
      : P
    : never;
}>[keyof T];

export function convert<
  T extends Object,
  U extends TreeProp<T>,
  R extends Object
>(
  list: T[] | undefined | null,
  callback: (item: T, opt: { pipe: T[]; parent?: any; level?: number }) => R,
  children: U = "children" as any,
  pop = false,
  pipe: T[] = [],
  parent: R | undefined = undefined,
  level: number = 0
) {
  if (!Array.isArray(list)) return [];
  return list.map((v) => {
    pipe.push(v);
    let t = callback(v, { pipe, parent, level }) as Tree<R>;
    if (Array.isArray(v[children])) {
      level++;
      t.children = convert(
        v[children] as any,
        callback,
        children,
        pop,
        pipe,
        t,
        level
      );
      level--;
    }
    pipe.pop();
    return t;
  });
}

/** 如果该属性值是undefined，则设置 */
export const setDefault = function <T, K extends keyof T>(
  target: T,
  prop: K,
  value: T[K]
) {
  target[prop] = target[prop] ?? value;
};

/** 赋值为null|undefined时，使用默认值 */
export function withDefault<T extends Object>(obj: T) {
  const initValue = { ...obj };
  return new Proxy(obj, {
    set(target, p, value) {
      target[p] = value ?? initValue[p];
      return true;
    },
    deleteProperty(target, p) {
      target[p] = initValue[p];
      return true;
    },
  }) as T;
}

export const replaceArr = <T>(arr: T[], newArr: Iterable<T>) => {
  arr.splice(0, arr.length, ...newArr);
  return arr;
};

export const toList = <T>(...all: (T | T[])[]) => all.flat(1);

export const _last = <T extends any[]>(arr: T): T[number] =>
  arr[arr.length - 1];

export const _ref = <T extends Object>(obj: T) => {
  const map = {
    _ref: obj,
    _replace: <U extends keyof T>(key: U, value: T[U]) =>
      (map[key as any] = value),
  };
  let p = new Proxy(obj, {
    get(tar, p) {
      if (p) {
        if (p == "_replace") return map._replace;
        if (Array.isArray(tar[p])) return map[p] ?? (map[p] = [...tar[p]]);
        if (typeof tar[p] === "object")
          return map[p] ?? (map[p] = _ref(tar[p]));
      }
    },
    set() {
      throw new Error("_ref only read,use _replace instead");
    },
  }) as Readonly<T & typeof map>; // todo: 这里的ts类型没有考虑到内部属性的类型改变,以及deepReadonly
  return p;
};

/** 注意: b可以是a的超集 */
export const _same = <T>(a: T, b: T): boolean => {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => _same(v, b[i]));
  }
  if (
    typeof a === "object" &&
    typeof b === "object" &&
    a !== null &&
    b !== null
  ) {
    return Object.keys(a).every((k) => _same(a[k], b[k]));
  }
  return false;
};

export const _ = lodash;

export const withResolve = <T>(
  fn: (a: (a?: T) => void) => void
): (() => Promise<T | undefined>) => {
  return () => new Promise((resolve, reject) => fn(resolve));
};

export const sideEffect = <T extends any[], R>(fn: (...a: T) => R) => {
  let effect;
  return (...a: T) => {
    if (effect) effect();
    return (effect = fn(...a));
  };
};

export const _chain = <T>(obj: T, key) => {
  if (!obj) return [];
  let arr = [obj];
  let head = obj;
  while (head[key]) {
    arr.push((head = head[key]));
  }
  return arr;
};

export const _pick = <T, K extends (keyof T)[]>(obj: T, keys: K) => {
  let a: Pick<T, K[number]> = {} as any;
  keys.forEach((k) => {
    a[k] = obj[k];
  });
  return a;
};

export function _find<T extends Object, K extends keyof T>(
  list: T[],
  value: T[K],
  key: K
) {
  let t;
  traverse(list, (v) => {
    if (v[key] === value) t = v;
  });
  return t as T | undefined;
}

export const _numInRange = (min, max) => {
  return (num: number) => Math.max(min, Math.min(max, num));
};
